import type { PointerEvent } from "react"

// Hero card hover: grows the card, tilts it toward the pointer and slides a
// white glare across it; a `.home-new-parallax` backdrop inside drifts the
// other way for depth. Cards come in two tilt variants: "small" for the
// single-slot cards and "large" for the wide Trails card, which only tilts a
// touch so its artwork does not swing. The transform and glare position are written straight
// to the element so pointer moves never re-render React; the card's
// transition class eases the motion. The amounts come from custom properties
// on the page wrapper, set in home-new.css. Honours reduced motion by leaving
// the card still.
export const tiltHoverClassName =
  "group relative transition-[transform,box-shadow] duration-(--card-ease) ease-out will-change-transform hover:z-10"

/** The glare overlay; place it as the last child of a card. */
export const glareClassName =
  "home-new-glare pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-(--card-ease) group-hover:opacity-(--glare-opacity)"

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

function readNumber(styles: CSSStyleDeclaration, name: string, fallback: number) {
  const value = Number.parseFloat(styles.getPropertyValue(name))
  return Number.isFinite(value) ? value : fallback
}

export type TiltVariant = "small" | "large"

/** Spread onto a card: its tilt variant plus the pointer handlers. */
export function tiltHoverProps(variant: TiltVariant) {
  return { "data-tilt": variant, ...tiltHoverHandlers }
}

export const tiltHoverHandlers = {
  onPointerMove(event: PointerEvent<HTMLElement>) {
    if (event.pointerType === "touch" || prefersReducedMotion()) return
    const element = event.currentTarget
    const bounds = element.getBoundingClientRect()
    const px = (event.clientX - bounds.left) / bounds.width
    const py = (event.clientY - bounds.top) / bounds.height
    const styles = getComputedStyle(element)
    const scale = readNumber(styles, "--card-hover-scale", 1.02)
    const maxTilt = readNumber(styles, "--card-max-tilt", 4)
    const perspective = readNumber(styles, "--card-perspective", 300)
    const rotateX = (-(py - 0.5) * maxTilt).toFixed(2)
    const rotateY = ((px - 0.5) * maxTilt).toFixed(2)
    element.style.transform = `perspective(${perspective}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(${scale})`
    element.style.setProperty("--glare-x", `${(px * 100).toFixed(1)}%`)
    element.style.setProperty("--glare-y", `${(py * 100).toFixed(1)}%`)
    // -0.5…0.5 against the pointer, for `.home-new-parallax` backdrops.
    element.style.setProperty("--parallax-x", (0.5 - px).toFixed(3))
    element.style.setProperty("--parallax-y", (0.5 - py).toFixed(3))
  },
  onPointerLeave(event: PointerEvent<HTMLElement>) {
    const element = event.currentTarget
    element.style.transform = ""
    element.style.removeProperty("--parallax-x")
    element.style.removeProperty("--parallax-y")
  },
}
